import { useState, useEffect } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Folder, FileText, ChevronRight, ChevronDown, Plus, Tag, Clock, Lock, Globe, Building2, FolderPlus, MoreVertical, Edit, Trash2, X } from 'lucide-react';
import { documentationAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import { useCompanyStore } from '../../context/companyStore';
import CompanyFilter from '../../components/common/CompanyFilter';
import { isInternalRole } from '../../utils/roles.js';

// Folder modal component
function FolderModal({ folder, parentId, onClose }) {
  const [name, setName] = useState(folder?.name || '');
  const [description, setDescription] = useState(folder?.description || '');
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data) => documentationAPI.createFolder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'folders'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create folder'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => documentationAPI.updateFolder(folder.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'folders'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update folder'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Folder name is required');
      return;
    }
    const data = { name: name.trim(), description: description.trim() || null, parentId };
    if (folder) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {folder ? 'Edit Folder' : 'New Folder'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Folder name"
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white rounded-lg"
            >
              {isLoading ? 'Saving...' : folder ? 'Save Changes' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete folder confirmation modal
function DeleteFolderModal({ folder, onClose }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: () => documentationAPI.deleteFolder(folder.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'folders'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to delete folder'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Delete Folder?</h2>
        <p className="text-neutral-600 dark:text-neutral-400 mb-4">
          Are you sure you want to delete "{folder.name}"? Articles in this folder will be moved to the root level.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
          >
            Cancel
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Visibility badge component
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Status badge component
function StatusBadge({ status }) {
  const config = {
    draft: { label: 'Draft', className: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300' },
    published: { label: 'Published', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    archived: { label: 'Archived', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  };
  const { label, className } = config[status] || config.draft;

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

// Folder tree item component
function FolderTreeItem({ folder, selectedFolderId, onSelect, level = 0, expandedFolders, toggleFolder, isInternal, onEdit, onDelete, onAddSubfolder }) {
  const isExpanded = expandedFolders.has(folder.id);
  const isSelected = selectedFolderId === folder.id;
  const hasChildren = folder.children && folder.children.length > 0;
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="group">
      <div
        className={`w-full flex items-center gap-1 px-2 py-1.5 rounded-lg text-left text-sm transition-colors
          ${isSelected
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400'
            : 'hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
          }`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); toggleFolder(folder.id); }}
            className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <button
          onClick={() => onSelect(folder.id)}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <Folder className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-neutral-400'}`} />
          <span className="truncate flex-1 text-left">{folder.name}</span>
        </button>
        {folder.article_count > 0 && (
          <span className="text-xs text-neutral-400">{folder.article_count}</span>
        )}
        {isInternal && (
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-1 opacity-0 group-hover:opacity-100 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded transition-opacity"
            >
              <MoreVertical className="w-3 h-3" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 min-w-[140px]">
                  <button
                    onClick={() => { setShowMenu(false); onAddSubfolder(folder.id); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                  >
                    <FolderPlus className="w-4 h-4" />
                    Add Subfolder
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); onEdit(folder); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); onDelete(folder); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {isExpanded && hasChildren && (
        <div>
          {folder.children.map(child => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              selectedFolderId={selectedFolderId}
              onSelect={onSelect}
              level={level + 1}
              expandedFolders={expandedFolders}
              toggleFolder={toggleFolder}
              isInternal={isInternal}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddSubfolder={onAddSubfolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Article card component
function ArticleCard({ article, isInternal }) {
  return (
    <Link
      to={`/documentation/articles/${article.id}`}
      className="block p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-brand-300 dark:hover:border-brand-600 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-neutral-900 dark:text-white truncate">{article.title}</h3>
          {article.excerpt && (
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 line-clamp-2">
              {article.excerpt}
            </p>
          )}
          <div className="flex items-center gap-3 mt-3 text-xs text-neutral-500 dark:text-neutral-400">
            {article.folder_name && (
              <span className="flex items-center gap-1">
                <Folder className="w-3 h-3" />
                {article.folder_name}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(article.updated_at).toLocaleDateString()}
            </span>
            {article.tags && article.tags.length > 0 && (
              <span className="flex items-center gap-1">
                <Tag className="w-3 h-3" />
                {article.tags.slice(0, 2).join(', ')}
                {article.tags.length > 2 && ` +${article.tags.length - 2}`}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {isInternal && (
            <>
              <VisibilityBadge visibility={article.visibility} />
              <StatusBadge status={article.status} />
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function DocumentationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [selectedFolderId, setSelectedFolderId] = useState(searchParams.get('folder') || null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [parentFolderIdForNew, setParentFolderIdForNew] = useState(null);
  const { user } = useAuthStore();
  const { selectedCompanyId } = useCompanyStore();

  const isInternal = isInternalRole(user?.role);

  const handleNewFolder = (parentId = null) => {
    setParentFolderIdForNew(parentId);
    setFolderToEdit(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setFolderToEdit(folder);
    setParentFolderIdForNew(folder.parent_id);
    setShowFolderModal(true);
  };

  const handleDeleteFolder = (folder) => {
    setFolderToDelete(folder);
  };

  const closeFolderModal = () => {
    setShowFolderModal(false);
    setFolderToEdit(null);
    setParentFolderIdForNew(null);
  };

  // Fetch folder tree (filtered by company for multi-company users)
  const { data: folderData } = useQuery({
    queryKey: ['documentation', 'folders', 'tree', selectedCompanyId],
    queryFn: () => documentationAPI.getFolderTree({ companyId: selectedCompanyId }),
  });

  // Fetch articles for selected company
  const debouncedSearch = useDebounce(search, 300);
  // Note: Documentation is kept separate per company - not combined
  const { data: articlesData, isLoading: articlesLoading } = useQuery({
    queryKey: ['documentation', 'articles', { companyId: selectedCompanyId, folderId: selectedFolderId, search: debouncedSearch }],
    queryFn: () => documentationAPI.listArticles({
      companyId: selectedCompanyId,
      folderId: selectedFolderId,
      search: debouncedSearch || undefined,
      limit: 50
    }),
  });

  // Fetch popular tags
  const { data: tagsData } = useQuery({
    queryKey: ['documentation', 'tags', 'popular'],
    queryFn: () => documentationAPI.getPopularTags(10),
  });

  const folders = folderData?.data?.tree || [];
  const articles = articlesData?.data?.articles || [];
  const pagination = articlesData?.data?.pagination;
  const popularTags = tagsData?.data?.tags || [];

  // Update URL when folder changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedFolderId) params.set('folder', selectedFolderId);
    if (search) params.set('q', search);
    setSearchParams(params, { replace: true });
  }, [selectedFolderId, search, setSearchParams]);

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already reactive through the query
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Documentation</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {isInternal ? 'Manage knowledge base articles and documentation' : 'Browse available documentation'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Company filter - documentation is separate per company */}
          <CompanyFilter />
          {isInternal && (
            <Link
              to="/documentation/articles/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Article
            </Link>
          )}
        </div>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documentation..."
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
      </form>

      <div className="flex gap-6">
        {/* Sidebar - Folder Tree */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-neutral-900 dark:text-white">Folders</h3>
              {isInternal && (
                <button
                  onClick={() => handleNewFolder(null)}
                  className="p-1 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors"
                  title="New Folder"
                >
                  <FolderPlus className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={() => setSelectedFolderId(null)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors mb-2
                ${selectedFolderId === null
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400'
                  : 'hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                }`}
            >
              <FileText className="w-4 h-4" />
              All Articles
            </button>
            <div className="space-y-0.5">
              {folders.map(folder => (
                <FolderTreeItem
                  key={folder.id}
                  folder={folder}
                  selectedFolderId={selectedFolderId}
                  onSelect={setSelectedFolderId}
                  expandedFolders={expandedFolders}
                  toggleFolder={toggleFolder}
                  isInternal={isInternal}
                  onEdit={handleEditFolder}
                  onDelete={handleDeleteFolder}
                  onAddSubfolder={handleNewFolder}
                />
              ))}
            </div>

            {/* Popular Tags */}
            {popularTags.length > 0 && (
              <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <h3 className="font-medium text-neutral-900 dark:text-white mb-3">Popular Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map(tag => (
                    <button
                      key={tag.id}
                      onClick={() => setSearch(`tag:${tag.name}`)}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded text-xs text-neutral-700 dark:text-neutral-300 transition-colors"
                    >
                      <Tag className="w-3 h-3" />
                      {tag.name}
                      <span className="text-neutral-400">({tag.article_count})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main content - Article list */}
        <div className="flex-1 min-w-0">
          {articlesLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 animate-pulse">
                  <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-2/3 mb-2" />
                  <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full mb-2" />
                  <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <>
              <div className="space-y-3">
                {articles.map(article => (
                  <ArticleCard key={article.id} article={article} isInternal={isInternal} />
                ))}
              </div>
              {pagination && pagination.pages > 1 && (
                <div className="mt-6 flex justify-center">
                  <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                    Page {pagination.page} of {pagination.pages} ({pagination.total} articles)
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <FileText className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                {search ? 'No articles found' : 'No articles yet'}
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                {search
                  ? 'Try adjusting your search terms'
                  : isInternal
                    ? 'Create your first article to get started'
                    : 'Check back later for documentation'
                }
              </p>
              {isInternal && !search && (
                <Link
                  to="/documentation/articles/new"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Article
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Folder Modal */}
      {showFolderModal && (
        <FolderModal
          folder={folderToEdit}
          parentId={parentFolderIdForNew}
          onClose={closeFolderModal}
        />
      )}

      {/* Delete Folder Modal */}
      {folderToDelete && (
        <DeleteFolderModal
          folder={folderToDelete}
          onClose={() => setFolderToDelete(null)}
        />
      )}
    </div>
  );
}
